"use client";

/**
 * AlertCellCarousel — preview area inside an AlertCellNode.
 *
 * Views are derived from the cell's content using a strict visual-priority
 * contract. Each kind of view gets its own little icon tab at the top of
 * the card, and the user can click any tab to jump to it. On the
 * /alerts/[id] route we also auto-advance the active tab every 3s with a
 * per-node stagger so the canvas feels alive but never flips in lockstep.
 *
 * Display contract (priority high → low):
 *   ┌───────────────────────────────────────────────────────────────────┐
 *   │  1. Dashboard          — KING. If present, renders alone, no tabs │
 *   │                          and no rotation. The final preview node  │
 *   │                          uses this for the "alert summary"        │
 *   │                          surface.                                 │
 *   │  2. Summary (purpose)  — always shown first in rotation when no   │
 *   │                          dashboard exists.                        │
 *   │  3. Visual output — unified_search / plotly / chart / df / image  │
 *   │  4. Code — always last in rotation. Stdout stays modal-only.      │
 *   └───────────────────────────────────────────────────────────────────┘
 *
 * Props:
 *   - `noRotate`          — never advance on a timer (still clickable)
 *   - `forceDashboardOnly` — collapse to a single dashboard view, no tabs
 *   - `showTabs`           — render the icon-tab bar at all
 *   - `variant`            — `"hero"` swaps in larger preview heights
 *   - `paused` / visibility — pause rotation (running alert, off-screen)
 */

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Prism as SyntaxHighlighterBase } from "react-syntax-highlighter";
import { materialDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import {
  FileText,
  Code2,
  LineChart,
  Terminal,
  LayoutDashboard,
  Maximize2,
  Table2,
  Search,
  Bot,
  Activity,
  type LucideIcon,
} from "lucide-react";
import {
  AlertCellMiniPreview,
  type AlertCellMiniPreviewProps,
} from "./AlertCellMiniPreview";
import {
  SignalRegistrationCard,
  type SignalRegisterData,
} from "@/components/notebook/SignalRegistrationCard";
import { DashboardView } from "@/components/notebook/DashboardView";
import { DashboardImage } from "@/components/notebook/DashboardImage";
import {
  isDashboardOutput,
  isNoiseStdoutText,
} from "@/lib/alert-output-detection";
import { alertLog, summarizeOutput } from "@/lib/alert-display-log";
import { createChannelLogger } from "@/lib/debug-log";

const dashScrollLog = createChannelLogger("dashboard_scroll");

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const SyntaxHighlighter = SyntaxHighlighterBase as any;

// Rotation pacing on /alerts (user-driven values — do NOT change without
// asking first).
//
// DESIGN GOAL: cards should feel like independent oscillators — each one
// picks its OWN random interval every time it ticks, with no global
// cadence tying them together. Two cards flipping almost simultaneously
// is fine; a card sitting on one tab for 5+ seconds while its neighbour
// flips twice is also fine. The point is to kill the old "wave" effect
// where cards flipped in a choreographed left-to-right sweep on a fixed
// period, which made the canvas feel mechanical.
//
// Implementation: we keep NO shared cadence at all. The old
// phaseIndex/PHASE_STEP_MS stagger is gone. Each card just re-rolls a
// fresh random delay from a wide uniform range after every tick.
//
// Tuning (sped up further — 2026-05-17 pass 2):
//   2026-05-17 pass 1 (800-2200 / 2200-4500) still read as too slow during
//   replay handoff. Pulled first-tick floor down to ~half a second and
//   capped the per-tick window around 2.5s so users see every tab cycle
//   while scanning a card. Still meaningfully above the rejected
//   2026-05-01 pace (300-1200 / 900-2200) so we keep enough headroom to
//   avoid the AnimatePresence-thrash load issue when many nodes are
//   mounted at once.
//
//   - first tick  : uniform in [500, 1400] ms
//   - per-tick    : uniform in [1400, 2600] ms
const FIRST_TICK_MIN_MS = 500;
const FIRST_TICK_MAX_MS = 1400;
const NEXT_TICK_MIN_MS = 1400;
const NEXT_TICK_MAX_MS = 2600;

function randBetween(minMs: number, maxMs: number): number {
  return minMs + Math.floor(Math.random() * (maxMs - minMs + 1));
}

function pickFirstIntervalMs(): number {
  return randBetween(FIRST_TICK_MIN_MS, FIRST_TICK_MAX_MS);
}

function pickNextIntervalMs(): number {
  return randBetween(NEXT_TICK_MIN_MS, NEXT_TICK_MAX_MS);
}

/**
 * Return the most useful starting index for the alert-page card.
 *
 * User expectation is "show me the output", so a real visual must beat
 * code every time. Stdout is intentionally excluded from collapsed
 * preview tabs; it remains available in the expanded modal.
 *
 * Returns `null` when the views list has no meaty tabs yet (typical
 * during the brief window before outputs arrive on first render). The
 * caller is expected to retry in an effect once views expands.
 */
function pickRandomMeatyIdx(
  views: ReadonlyArray<{ kind: string }>,
): number | null {
  const visualIdx = views.findIndex((v) => v.kind === "visual");
  if (visualIdx >= 0) return visualIdx;
  const codeIdx = views.findIndex((v) => v.kind === "code");
  if (codeIdx >= 0) return codeIdx;
  return null;
}

const SHELL_MAX = 184;
const SHELL_MAX_HERO = 340;

// ─────────────────────────────────────────────────────────────────────────
// View model
// ─────────────────────────────────────────────────────────────────────────

// Collapsed previews show Summary / Visual / Code.
// Stdout is kept as a renderable kind for legacy/internal callers but is no
// longer added to the alert-card preview tab list.
// Dashboard remains the hero-only path.
// All visual outputs (plotly / bokeh / chart html / dataframe html / image /
// unified_search / agent carousels) collapse into the SINGLE "Visual" tab.
// First matching output in the cell wins for that tab's content.
type ViewKind =
  | "summary"
  | "visual"
  | "stdout"
  | "code"
  | "dashboard"; // hero-only path

interface View {
  kind: ViewKind;
  /** For output-backed views, the original index inside the `outputs` array
   *  so <AlertCellMiniPreview> can be handed the exact output to render. */
  outputIndex?: number;
  /** Pre-resolved label for the icon-tab tooltip / aria. */
  label: string;
  /** Human shown in a11y. */
  title: string;
  /** Optional icon override. Used so multiple "visual" tabs (plotly vs. df
   *  vs. image) show distinct icons in the tab bar. Falls back to
   *  `iconFor(kind)` when unset. */
  iconOverride?: LucideIcon;
}

const KIND_LABEL: Record<ViewKind, { label: string; title: string }> = {
  dashboard: { label: "Dashboard", title: "Dashboard preview" },
  summary: { label: "Summary", title: "What this step does" },
  visual: { label: "Visual", title: "Plot / chart / table" },
  stdout: { label: "Stdout", title: "Printed output" },
  code: { label: "Code", title: "Python code" },
};

/**
 * Each rich visual output (plot, chart, dataframe, image, unified search,
 * agent carousel) becomes ITS OWN tab on the carousel so the user sees
 * every visual as the rotation cycles through. Dashboards are excluded on
 * purpose — those live on the hero cell and don't appear as a tab.
 *
 * Returned in source-index order (stable). Caller pushes them into the
 * views list between Summary and Code.
 */
type VisualSlot = {
  index: number;
  icon: LucideIcon;
  title: string;
};
function allVisualOutputs(
  outputs: { type?: string; data?: unknown }[] | undefined,
): VisualSlot[] {
  if (!Array.isArray(outputs)) return [];
  const slots: VisualSlot[] = [];
  for (let i = 0; i < outputs.length; i++) {
    const o = outputs[i];
    if (!o || isDashboardOutput(o)) continue;
    const t = o.type;
    if (t === "signal_register") {
      slots.push({ index: i, icon: Activity, title: "Signal" });
      continue;
    }
    if (t === "unified_search") {
      slots.push({ index: i, icon: Search, title: "Search results" });
      continue;
    }
    // Plotly always wins — user feedback: "always choose plotly".
    // PKA emits plotly alongside a PNG fallback; the PNG is a static
    // snapshot that doesn't read as well as the real interactive chart
    // at preview size. So plotly gets its own Visual tab.
    if (t === "plotly") {
      slots.push({ index: i, icon: LineChart, title: "Chart" });
      continue;
    }
    if (t === "bokeh" || t === "backtest-tabs") {
      continue;
    }
    if (t === "image") {
      // Skip image when plotly is also present in the same cell —
      // the PNG is just a fallback snapshot of the same chart. If a
      // cell only has an image (no plotly), we'll still pick it up
      // in a second pass below.
      const hasPlotlyPeer = outputs.some((x) => x && x.type === "plotly");
      if (hasPlotlyPeer) continue;
      slots.push({ index: i, icon: LineChart, title: "Image" });
      continue;
    }
    if (t === "fund_agent" || t === "sub_agent") {
      slots.push({ index: i, icon: Bot, title: "Agent results" });
      continue;
    }
    if (t === "html" && typeof o.data === "string") {
      // Skip backtest-chart html for the same reason as plotly above.
      if (/data-visualization-type="backtest-tabs"/i.test(o.data)) {
        continue;
      }
      if (/<table/i.test(o.data)) {
        slots.push({ index: i, icon: Table2, title: "Dataframe" });
      }
    }
  }
  return slots;
}

function iconFor(kind: ViewKind) {
  switch (kind) {
    case "dashboard":
      return LayoutDashboard;
    case "summary":
      return FileText;
    case "visual":
      return LineChart;
    case "stdout":
      return Terminal;
    case "code":
      return Code2;
    default:
      return FileText;
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Props
// ─────────────────────────────────────────────────────────────────────────

export interface AlertCellCarouselProps {
  purpose: string;
  /**
   * Rich "**What it does**" / "**What it produces**" markdown generated
   * by the backend at alert upload time. Rendered in the Summary tab
   * when present. If null/empty, we fall back to the short `purpose`
   * string (cell title) so legacy alerts still read cleanly while their
   * descriptions are being lazily backfilled.
   */
  description?: string | null;
  code: string;
  stdout?: string;
  outputs?: AlertCellMiniPreviewProps["outputs"];
  status: AlertCellMiniPreviewProps["status"];
  /**
   * Freeze rotation (e.g., while the alert is actively running on the server,
   * or when a dropdown is open). When paused, auto-advance stops; tabs are
   * still clickable.
   */
  paused?: boolean;
  /**
   * Per-node phase offset index (0-based). Adjacent nodes use different
   * phase indexes so rotations don't happen in lockstep.
   */
  phaseIndex?: number;
  /**
   * Disable auto-advance entirely. On /chat alert-mode sessions this is
   * true (static best view). On /alerts it is false (rotate every 3s).
   */
  noRotate?: boolean;
  /**
   * Render the icon-tab bar along the top. Hidden on dashboard-king
   * cards where there's only one view to show anyway.
   */
  showTabs?: boolean;
  /**
   * Collapse the carousel to a SINGLE dashboard view. Used for the final
   * preview node on /alerts (hero) and for any cell anywhere that has a
   * dashboard output — the dashboard is always the hero.
   */
  forceDashboardOnly?: boolean;
  /**
   * Visual size preset. "hero" uses a taller preview shell so the dashboard
   * preview actually breathes. "default" is the normal compact shell.
   */
  variant?: "default" | "hero";
  /**
   * Optional handler for the expand button on the right edge of the tab
   * bar. When omitted no expand button is rendered. Clicking it should
   * open the node's modal without triggering tab rotation.
   */
  onExpand?: () => void;
  /**
   * When true AND the current view is the hero dashboard, the dashboard
   * preview ping-pongs its translateY through its full vertical extent
   * so the card feels alive (match the other cards' carousel animation).
   * Disable on /chat alert-mode where static previews are preferred.
   */
  autoScrollDashboard?: boolean;
  viewMode?: string;
}

// ─────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────

export function AlertCellCarousel({
  purpose,
  description = null,
  code,
  stdout,
  outputs,
  status,
  paused = false,
  phaseIndex = 0,
  noRotate = false,
  showTabs = true,
  forceDashboardOnly = false,
  variant = "default",
  onExpand,
  autoScrollDashboard = false,
  viewMode,
}: AlertCellCarouselProps) {
  const isHero = variant === "hero";
  const shellMax = isHero ? SHELL_MAX_HERO : SHELL_MAX;
  // Per-instance stable id used to scope framer-motion's `layoutId` for
  // the active-tab underline. Previously every card on the page shared
  // the literal `layoutId="alert-tab-underline"`, which put all cards
  // into ONE global layout group — meaning when card A's tab changed,
  // framer could animate its underline to or from card B's underline
  // position. The result was the "switches but stays on the same tab"
  // glitch: the underline appeared frozen on the old tab (because it
  // was busy flying across the screen to a neighbour). Scoping the id
  // per instance keeps each card's underline self-contained.
  const underlineId = useId();

  // ── Views ──────────────────────────────────────────────────────────────
  // SIMPLIFIED contract — fixed preview tabs in a fixed order:
  //
  //   1. Summary — always shown (uses the cell's description/purpose)
  //   2. Visual  — ONE tab that collapses plotly / chart / df / image /
  //                unified_search / agent results into a single "best
  //                visual for this cell". First matching output wins.
  //   3. Code    — always shown when there is code to display
  //
  // Stdout is intentionally not a collapsed-card tab. It stays available
  // from the expanded modal/details view where debugging output belongs.
  //
  // Dashboard cells short-circuit to a single dashboard view with NO
  // tabs rendered (the node-level prop `showTabs` is false there).
  const views = useMemo<View[]>(() => {
    const safeOutputs = Array.isArray(outputs) ? outputs : [];

    // Dashboard-king short-circuit.
    const dashIdx = safeOutputs.findIndex((o) => isDashboardOutput(o));
    if (forceDashboardOnly) {
      if (dashIdx >= 0) {
        return [
          {
            kind: "dashboard",
            outputIndex: dashIdx,
            ...KIND_LABEL.dashboard,
          },
        ];
      }
      // Signal canvases have no Gemini dashboard — the signal-registration
      // card IS the hero. Collapse to a single signal view so the hero node
      // shows the card full-bleed (no tabs), mirroring the dashboard king.
      const sigIdx = safeOutputs.findIndex((o) => o.type === "signal_register");
      if (sigIdx >= 0) {
        return [
          {
            kind: "visual",
            outputIndex: sigIdx,
            label: "Signal",
            title: "Signal",
            iconOverride: Activity,
          },
        ];
      }
      // Caller insisted on dashboard-only but the cell doesn't have one.
      // Fall through to normal view building so we don't render a broken
      // empty card — the dashboard flag will be noisy in logs below.
    }

    const list: View[] = [];

    // 1. Summary — always FIRST when we have EITHER a rich description or
    //    at least the fallback cell title. Rich description preferred; if
    //    missing (legacy alert mid-backfill) we still show the purpose
    //    so Summary is never silently empty.
    const hasSummary =
      (description && description.trim().length > 0) ||
      (purpose && purpose.trim().length > 0);
    if (hasSummary) {
      list.push({ kind: "summary", ...KIND_LABEL.summary });
    }

    // 2. Visual — ONE tab per visual output so the rotation cycles
    //    through every plot / dataframe / image the cell produced.
    //    Each tab gets a type-specific icon (LineChart for plots,
    //    Table2 for dataframes, Image for raster, Search for search,
    //    Bot for agent runs) so the icon bar hints at what's inside
    //    without the user waiting for the tab to flip.
    //    Hard cap at 4 visuals to keep the tab bar readable; the
    //    remainder is still visible in the expanded modal.
    const visualSlots = allVisualOutputs(safeOutputs).slice(0, 4);
    for (const slot of visualSlots) {
      list.push({
        kind: "visual",
        outputIndex: slot.index,
        label: "Visual",
        title: slot.title,
        iconOverride: slot.icon,
      });
    }

    // 3. Code — always last when code exists.
    if (code && code.trim()) {
      list.push({ kind: "code", ...KIND_LABEL.code });
    }

    // Guarantee at least ONE view so we never render an empty shell.
    if (list.length === 0) {
      list.push({
        kind: "summary",
        label: "Summary",
        title: "What this step does",
      });
    }
    return list;
  }, [purpose, description, code, outputs, forceDashboardOnly]);

  // ── Active-tab state ───────────────────────────────────────────────────
  //
  // Starting tab selection:
  //   - When rotation IS enabled (the /alerts page): each card picks a
  //     RANDOM "meaty" tab (Visual / Code) at mount so all four
  //     cards don't boot on the same Summary tab. Summary is deliberately
  //     excluded from the initial pool — cards still rotate THROUGH
  //     Summary, just don't all open on it at t=0. Falls back to views[0]
  //     for the rare cell that only has a Summary tab.
  //   - When rotation is DISABLED (noRotate === true — i.e. /chat
  //     alert-mode): start on the MOST INTERESTING tab for this cell
  //     since there's no rotation to ever get to anything else.
  //     Priority: Visual → Summary → Code.
  //     Rationale: on /chat the canvas is an artifact panel beside the
  //     chat column, the user wants a glance-level "what did this step
  //     produce?" answer. Charts/tables/plots answer that best.
  //     Summary/Code are fallbacks for config/setup cells that don't
  //     generate a visual.
  const preferredStartIdx = useMemo(() => {
    if (!noRotate) return 0; // rotating cards use a separate random-init path below
    const visualIdx = views.findIndex((v) => v.kind === "visual");
    if (visualIdx >= 0) return visualIdx;
    const summaryIdx = views.findIndex((v) => v.kind === "summary");
    if (summaryIdx >= 0) return summaryIdx;
    const codeIdx = views.findIndex((v) => v.kind === "code");
    if (codeIdx >= 0) return codeIdx;
    return 0;
  }, [noRotate, views]);

  // One-shot random start for rotating carousels. We capture it in a ref
  // (not state) so we can lazily re-pick if `views` only contains Summary
  // on first mount and the meaty tabs arrive shortly after from
  // parent-side output loading. `null` means "haven't picked yet".
  const randomStartRef = useRef<number | null>(null);

  const [idx, setIdx] = useState<number>(() => {
    if (noRotate) return preferredStartIdx;
    // Initial pick at mount — may return 0 if the meaty tabs don't exist
    // yet; the effect below will re-pick once they arrive.
    const meaty = pickRandomMeatyIdx(views);
    if (meaty != null) {
      randomStartRef.current = meaty;
      return meaty;
    }
    return 0;
  });

  // When the preferred starting index changes (e.g. outputs finished
  // loading and a visual tab became available), snap to it — but only
  // when rotation is disabled. On /alerts we never override the user's
  // carousel position since auto-advance owns it.
  useEffect(() => {
    if (!noRotate) return;
    setIdx(preferredStartIdx);
  }, [noRotate, preferredStartIdx]);

  // Rotating carousels only: if we couldn't pick a random meaty start at
  // mount (views was incomplete), watch for views to gain a meaty tab
  // and commit the random pick exactly once. After that, rotation owns
  // idx and we never forcibly move it again.
  useEffect(() => {
    if (noRotate) return;
    if (randomStartRef.current != null) return;
    const meaty = pickRandomMeatyIdx(views);
    if (meaty == null) return;
    randomStartRef.current = meaty;
    setIdx(meaty);
  }, [noRotate, views]);

  // If the list of views shrinks underneath us (e.g., outputs disappeared
  // between polls), clamp the active index so we don't render an undefined
  // view for one frame.
  useEffect(() => {
    if (idx >= views.length) setIdx(noRotate ? preferredStartIdx : 0);
  }, [views.length, idx, noRotate, preferredStartIdx]);

  // ── Diagnostics ────────────────────────────────────────────────────────
  useEffect(() => {
    const first = views[0];
    const outputSummary =
      first?.outputIndex != null && Array.isArray(outputs)
        ? summarizeOutput(outputs[first.outputIndex])
        : undefined;
    alertLog("carousel:views_built", {
      noRotate,
      paused,
      forceDashboardOnly,
      variant,
      views_count: views.length,
      first_view_kind: first?.kind,
      first_view_output_summary: outputSummary,
      outputs_count: Array.isArray(outputs) ? outputs.length : 0,
      outputs: (Array.isArray(outputs) ? outputs : []).map(summarizeOutput),
    });
  }, [views, noRotate, paused, outputs, forceDashboardOnly, variant]);

  // ── Visibility gating ──────────────────────────────────────────────────
  const rootRef = useRef<HTMLDivElement | null>(null);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          setVisible(e.isIntersecting);
          alertLog("carousel:visibility_io", {
            phaseIndex,
            isIntersecting: e.isIntersecting,
            intersectionRatio: e.intersectionRatio,
          });
        }
      },
      { threshold: 0.1 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [phaseIndex]);

  useEffect(() => {
    const onVis = () => {
      const hidden = document.visibilityState === "hidden";
      setVisible(!hidden);
      alertLog("carousel:visibility_doc", {
        phaseIndex,
        visibilityState: document.visibilityState,
        setVisibleTo: !hidden,
      });
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [phaseIndex]);

  // ── Auto-advance (only on /alerts, noRotate === false) ────────────────
  // Every tick uses a FRESH random interval in the [base - jitter, base +
  // jitter] range. This means nodes that started in sync drift apart
  // within one or two cycles and never lock step again. Combined with the
  // per-node phase offset on the first tick, this makes the canvas feel
  // organic rather than robotic.
  useEffect(() => {
    // One compact log entry per effect run so when rotation is stuck we
    // can see WHICH gate bailed. `decision` = "scheduled" means the
    // timeout chain started; anything else is the reason rotation is
    // currently killed on this card.
    const gateState = {
      phaseIndex,
      noRotate,
      paused,
      visible,
      views_count: views.length,
      view_kinds: views.map((v) => v.kind),
      forceDashboardOnly,
    };

    if (noRotate) {
      alertLog("carousel:rotate_effect", {
        ...gateState,
        decision: "skipped:noRotate",
      });
      return;
    }
    if (paused || !visible) {
      alertLog("carousel:rotate_effect", {
        ...gateState,
        decision: paused ? "skipped:paused" : "skipped:not_visible",
      });
      return;
    }
    if (views.length <= 1) {
      alertLog("carousel:rotate_effect", {
        ...gateState,
        decision: "skipped:single_view",
      });
      return;
    }

    // No phase offset — cards are independent oscillators. Each one
    // just schedules its FIRST tick from a wide random range and then
    // re-rolls after every subsequent tick. `phaseIndex` is still
    // accepted as a prop for callers that may use it to inject
    // per-instance variety elsewhere, but we deliberately do NOT consume
    // it in the timing math anymore.
    const firstDelay = pickFirstIntervalMs();

    alertLog("carousel:rotate_effect", {
      ...gateState,
      decision: "scheduled",
      firstDelay,
    });

    let timeoutId: number | undefined;
    const scheduleNext = (delay: number) => {
      timeoutId = window.setTimeout(() => {
        setIdx((i) => {
          const next = (i + 1) % views.length;
          alertLog("carousel:tick_fired", {
            phaseIndex,
            from_idx: i,
            to_idx: next,
            from_kind: views[i]?.kind,
            to_kind: views[next]?.kind,
          });
          return next;
        });
        scheduleNext(pickNextIntervalMs());
      }, delay);
    };
    // First tick = a random delay in [FIRST_TICK_MIN_MS, FIRST_TICK_MAX_MS].
    // No shared cadence, no phase offset — each card boots on its own
    // schedule and stays independent for the lifetime of the component.
    scheduleNext(firstDelay);

    return () => {
      if (timeoutId !== undefined) {
        alertLog("carousel:rotate_effect_cleanup", {
          phaseIndex,
          reason: "deps_changed_or_unmount",
        });
        window.clearTimeout(timeoutId);
      }
    };
    // We intentionally exclude `views` and `forceDashboardOnly` from deps
    // to keep the original rotation-reset semantics: only length changes
    // should tear down the timeout chain. `views` is still read inside
    // the setter closure for the kind log — that reads the latest render's
    // views via the closure capture, which is fine for a log-only use.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paused, visible, views.length, phaseIndex, noRotate]);

  const current = views[Math.min(idx, views.length - 1)];

  // ── Render ─────────────────────────────────────────────────────────────
  const ctx = {
    purpose,
    description,
    code,
    stdout,
    outputs,
    status,
    shellMax,
    isHero,
    autoScrollDashboard,
    paused,
    viewMode,
    onExpand,
  };
  const onTabClick = useCallback((i: number) => {
    setIdx(i);
  }, []);

  // Static (dashboard-king / /chat alert-mode) — no tabs, no crossfade.
  if (views.length === 1 || !showTabs) {
    return (
      <div ref={rootRef} className="relative">
        {renderView(current, ctx)}
      </div>
    );
  }

  return (
    <div ref={rootRef} className="relative">
      {/* Minimalist icon-tab bar — flat, no border, no pill. Icons only,
          small size, spread from left with the expand button pinned to
          the far right:   [icon1] [icon2] [icon3] [icon4] ─── [expand] */}
      <TabBar
        views={views}
        activeIdx={Math.min(idx, views.length - 1)}
        onClick={onTabClick}
        onExpand={onExpand}
        underlineId={underlineId}
        viewMode={viewMode}
      />

      <AnimatePresence mode="wait">
        <motion.div
          // Include the active tab index in the key so every rotation
          // forces a fresh motion.div (and thus a real fade transition),
          // even in the unlikely case that two consecutive views share
          // the same kind+outputIndex.
          key={`${idx}-${current.kind}-${current.outputIndex ?? "none"}`}
          initial={{ opacity: 0, y: 3 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -3 }}
          transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
        >
          {renderView(current, ctx)}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// Tab bar
// ─────────────────────────────────────────────────────────────────────────

function TabBar({
  views,
  activeIdx,
  onClick,
  onExpand,
  underlineId,
  viewMode,
}: {
  views: View[];
  activeIdx: number;
  onClick: (i: number) => void;
  viewMode?: string;
  onExpand?: () => void;
  /** Per-carousel unique id used to scope the active-tab underline's
   *  framer-motion `layoutId`. Prevents cross-card underline animation
   *  when multiple AlertCellCarousels share a page. */
  underlineId: string;
}) {
  return (
    <div
      className="mb-2 flex items-center gap-2.5"
      role="tablist"
      aria-label="Cell view tabs"
    >
      {views.map((v, i) => {
        const Icon = v.iconOverride ?? iconFor(v.kind);
        const active = i === activeIdx;
        return (
          <button
            key={`${v.kind}-${v.outputIndex ?? i}`}
            type="button"
            role="tab"
            aria-selected={active}
            aria-label={v.title}
            title={v.title}
            onClick={(e) => {
              e.stopPropagation();
              onClick(i);
            }}
            className={[
              "relative inline-flex items-center justify-center w-[18px] h-[18px] transition-colors duration-200",
              // No pill, no border, no background — just colored icon.
              active
                ? viewMode === "signal" ? "text-emerald-100" : "text-sky-100"
                : viewMode === "signal" ? "text-slate-500 hover:text-emerald-200" : "text-slate-500 hover:text-sky-200",
            ].join(" ")}
          >
            <Icon className="w-[14px] h-[14px]" strokeWidth={2} />
            {active && (
              <motion.span
                layoutId={`alert-tab-underline-${underlineId}`}
                className={`absolute -bottom-1 left-0 right-0 h-[1.5px] rounded-full ${viewMode === "signal" ? "bg-emerald-200" : "bg-sky-200"}`}
                transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              />
            )}
          </button>
        );
      })}

      {/* Spacer pushes the expand button to the far right. */}
      <span className="flex-1" />

      {onExpand && (
        <button
          type="button"
          aria-label="Expand step"
          title="Expand"
          onClick={(e) => {
            e.stopPropagation();
            onExpand();
          }}
          className={`inline-flex items-center justify-center w-[18px] h-[18px] text-slate-500 transition-colors ${viewMode === "signal" ? "hover:text-emerald-200" : "hover:text-sky-200"}`}
        >
          <Maximize2 className="w-[13px] h-[13px]" strokeWidth={2} />
        </button>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
// View renderers
// ─────────────────────────────────────────────────────────────────────────

function renderView(
  view: View,
  ctx: {
    purpose: string;
    description?: string | null;
    code: string;
    stdout?: string;
    outputs?: AlertCellMiniPreviewProps["outputs"];
    status: AlertCellMiniPreviewProps["status"];
    shellMax: number;
    isHero: boolean;
    autoScrollDashboard?: boolean;
    paused?: boolean;
    viewMode?: string;
    onExpand?: () => void;
  },
) {
  if (view.kind === "summary") {
    return (
      <SummaryView
        description={ctx.description}
        fallbackTitle={ctx.purpose}
        shellMax={ctx.shellMax}
        viewMode={ctx.viewMode}
      />
    );
  }
  if (view.kind === "code") {
    return <CodeView code={ctx.code} shellMax={ctx.shellMax} viewMode={ctx.viewMode} />;
  }
  if (view.kind === "stdout") {
    // Prefer the stdout output found in the outputs array; fall back to
    // the pre-extracted stdout string on the node data if caller didn't
    // pass a resolved output index.
    let text = ctx.stdout || "";
    if (view.outputIndex != null && Array.isArray(ctx.outputs)) {
      const o = ctx.outputs[view.outputIndex];
      if (o && typeof o.data === "string") text = o.data;
    }
    return <StdoutView text={text} shellMax={ctx.shellMax} viewMode={ctx.viewMode} />;
  }
  // Hero signal card — render the full SignalRegistrationCard at hero scale
  // (scrollable) so the big "Final Results" node is filled with the run
  // summary + positions instead of a tiny clamped mini-preview.
  if (ctx.isHero && view.kind === "visual") {
    const sig =
      view.outputIndex != null ? ctx.outputs?.[view.outputIndex] : undefined;
    if (sig && (sig as { type?: string }).type === "signal_register") {
      return (
        <div className="h-full w-full p-1">
          <SignalRegistrationCard
            data={(sig as { data?: SignalRegisterData }).data as SignalRegisterData}
            hero
            onViewDashboard={ctx.onExpand}
          />
        </div>
      );
    }
  }
  // Hero dashboard — render the raw dashboard HTML directly at a bigger
  // size so the preview actually fills the hero card instead of leaving
  // cyan "dead space" around a tiny mini-preview.
  if (view.kind === "dashboard" && ctx.isHero) {
    const one =
      view.outputIndex != null ? ctx.outputs?.[view.outputIndex] : undefined;
    if (one)
      return (
        <DashboardHeroView
          output={one}
          autoScroll={Boolean(ctx.autoScrollDashboard) && !ctx.paused}
        />
      );
  }
  // Non-hero dashboard (e.g. /chat alert-mode) — render a COMPACT dashboard
  // preview. Without this branch the carousel collapsed to a single
  // dashboard view, then fell through to AlertCellMiniPreview, which has no
  // dashboard renderer and ended up showing the cell's stdout
  // ("Alert condition met! Sending trigger email…") as the "preview" —
  // the user-reported "no dashboard preview" bug. Reuse the same iframe
  // DashboardView the hero + modal use, just clamped to the compact shell.
  if (view.kind === "dashboard") {
    const one =
      view.outputIndex != null ? ctx.outputs?.[view.outputIndex] : undefined;
    if (one) return <DashboardMiniView output={one} shellMax={ctx.shellMax} />;
  }
  // Output-backed view — delegate to the real MiniPreview with a
  // single-output slice so the renderer picks the right branch
  // (chart / dataframe / plotly / dashboard).
  const one =
    view.outputIndex != null ? ctx.outputs?.[view.outputIndex] : undefined;
  return (
    <AlertCellMiniPreview
      outputs={one ? [one] : []}
      stdout={ctx.stdout}
      code={ctx.code}
      status={ctx.status}
    />
  );
}

/**
 * DashboardHeroView — renders the dashboard HTML at hero scale.
 *
 * Delegates to <DashboardView>, which is the same iframe-backed renderer used
 * by the main notebook. Previously this component used
 * `dangerouslySetInnerHTML` + a regex-based CSS rescope to prevent the
 * dashboard's global `body { padding: 28px 32px 24px; }` rule from leaking
 * into the host page. That approach also forced the browser to repaint a
 * ~5000-node subtree (Plotly SVG + HTML tables + scripts) every frame while
 * dragging the React Flow hero node, making drag visibly laggy.
 *
 * Moving to an iframe fixes both at once:
 *   1. CSS isolation — the dashboard has its own `<body>`, so `body { ... }`
 *      rules can never touch the app's real body. No regex rescope needed.
 *   2. Drag performance — from the parent DOM's perspective the iframe is a
 *      single compositing layer. React Flow's transform on drag retranslates
 *      one rectangle instead of recompositing thousands of plotly + table
 *      elements. ~10–20× faster in practice.
 *
 * A `pointer-events-none` wrapper ensures iframe clicks fall through to the
 * node so the "click card to expand" UX still works. The hero shell
 * (460–520px) still clamps visual height; DashboardView reports its own
 * scroll height back via postMessage but CSS `max-height` caps it.
 */
function DashboardHeroView({
  output,
  autoScroll = false,
}: {
  output: { type?: string; data?: unknown };
  /** When true, ping-pong the dashboard vertically inside its fixed shell
   *  so the preview feels alive (matches the tab-rotation animation on
   *  non-dashboard cards). Only wired on /alerts; /chat alert-mode keeps
   *  the dashboard static. */
  autoScroll?: boolean;
}) {
  // NEW PATH — single PNG dashboard from GPT-5.4 Image 2. Wins over any
  // html path because it's the richer visual AND it doesn't need the
  // iframe height-reporting dance. The useMemo keeps the same shape as
  // the HTML branch so later hooks don't care which we returned.
  const imageData = useMemo(() => {
    if (output.type !== "dashboard_image") return null;
    const d = output.data as
      | { url?: string; generated_at?: string | null; model?: string | null }
      | undefined;
    if (!d || typeof d.url !== "string" || !d.url) return null;
    return {
      url: d.url,
      generatedAt: d.generated_at ?? null,
      model: d.model ?? null,
    };
  }, [output]);

  const html = useMemo(() => {
    if (imageData) return ""; // image path; HTML unused
    if (output.type === "dashboard") {
      return String(output.data ?? "");
    }
    // html-marker path — strip the outer <div data-xynth-component="dashboard">
    // wrapper so we don't double-wrap.
    const raw = String(output.data ?? "");
    const m = raw.match(
      /<div\s+data-xynth-component="dashboard">([\s\S]*)<\/div>\s*$/i,
    );
    return m ? m[1] : raw;
  }, [output, imageData]);

  // ── Auto-scroll animation ─────────────────────────────────────────────
  // The outer wrapper is clamped to ~460–520px by the hero shell, but the
  // dashboard iframe grows to its full content height (DashboardView sets
  // iframe.height = content.scrollHeight). So the "scrollable" region is
  // the difference between the inner track's scrollHeight and the outer
  // wrapper's clientHeight. We measure both with a ResizeObserver.
  const wrapperRef = useRef<HTMLDivElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  const [maxScroll, setMaxScroll] = useState(0);

  useEffect(() => {
    if (!autoScroll) {
      setMaxScroll(0);
      return;
    }
    const wrapper = wrapperRef.current;
    const inner = innerRef.current;
    if (!wrapper || !inner || typeof ResizeObserver === "undefined") return;

    const compute = () => {
      const w = wrapper.clientHeight;
      const h = inner.scrollHeight;
      const next = Math.max(0, h - w);
      dashScrollLog("compute", {
        wrapperClientHeight: w,
        innerScrollHeight: h,
        newMaxScroll: next,
      });
      setMaxScroll((prev) => (Math.abs(prev - next) > 1 ? next : prev));
    };

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(wrapper);
    ro.observe(inner);
    // The iframe's height can grow AFTER the first paint (plotly mounts,
    // dashboard reports height back via postMessage). Retry for a few
    // seconds so we catch late layout.
    const timers = [400, 1200, 3000, 6000].map((ms) =>
      window.setTimeout(compute, ms),
    );

    return () => {
      ro.disconnect();
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, [autoScroll, html]);

  // Only animate when there's meaningful room to scroll. Below 60px the
  // motion reads as jitter.
  const shouldAnimate = autoScroll && maxScroll > 60;
  // Dashboard scroll behavior (scaled back 2026-05-02 — the 60 px/s rate
  // from 2026-05-01 was too aggressive on tall HTML dashboards and put
  // continuous transform load on the page in production).
  //
  // Behavior contract (unchanged from 2026-05-01):
  //   1. Scroll down all the way (linear, no holds).
  //   2. SNAP back to the top (instant — `repeatType: "loop"` re-runs
  //      the keyframes from the start, which produces the snap).
  //   3. Scroll down again. Loop forever.
  //
  // Active scroll rate target: ~20 px/s. That gives:
  //   • 500px scroll  → 25s per cycle
  //   • 1000px scroll → 50s per cycle
  //   • 5000px scroll → capped at 180s
  //
  // Clamp 18s–180s so motion is always perceptible but never a
  // constant-flow distraction.
  const cycleDuration = shouldAnimate
    ? Math.min(180, Math.max(18, maxScroll / 20))
    : 0;

  useEffect(() => {
    dashScrollLog("anim_params", {
      autoScroll,
      maxScroll,
      shouldAnimate,
      cycleDuration_sec: cycleDuration,
      cycleDuration_min: Math.round((cycleDuration / 60) * 10) / 10,
      imagePath: Boolean(imageData),
    });
  }, [autoScroll, maxScroll, shouldAnimate, cycleDuration, imageData]);

  return (
    <div
      ref={wrapperRef}
      className="relative rounded-lg border border-slate-200/15 bg-slate-950/70 overflow-hidden pointer-events-none select-none"
      style={{ minHeight: 460, maxHeight: 520 }}
    >
      <motion.div
        ref={innerRef}
        // One-way scroll with snap-back loop:
        //   y: 0 → -maxScroll  (scroll all the way down, linear)
        //   then `repeatType: "loop"` re-runs the same animation from
        //   y: 0, which presents as an instant snap from -maxScroll
        //   back to 0 between iterations. This matches the user's
        //   "scroll down all the way then appear at the top again
        //   and then scroll through" requirement.
        animate={
          shouldAnimate
            ? { y: [0, -maxScroll] }
            : { y: 0 }
        }
        transition={
          shouldAnimate
            ? {
                duration: cycleDuration,
                repeat: Infinity,
                repeatType: "loop",
                ease: "linear",
                times: [0, 1],
              }
            : { duration: 0.2 }
        }
        style={{ willChange: shouldAnimate ? "transform" : undefined }}
      >
        {imageData ? (
          <DashboardImage
            url={imageData.url}
            generatedAt={imageData.generatedAt}
            model={imageData.model}
            noBorder
          />
        ) : (
          <DashboardView html={html} noBorder />
        )}
      </motion.div>
    </div>
  );
}

/**
 * DashboardMiniView — compact dashboard preview for non-hero nodes
 * (the /chat alert-mode canvas). Mounts the SAME iframe-backed
 * <DashboardView> the hero + modal use so the dashboard renders for real
 * (Plotly + tables), just clamped to the compact node shell with a soft
 * bottom fade. Handles both the new single-PNG dashboard
 * (`dashboard_image`) and the legacy HTML dashboard (`dashboard` /
 * html-marker) paths.
 */
function DashboardMiniView({
  output,
  shellMax,
}: {
  output: { type?: string; data?: unknown };
  shellMax: number;
}) {
  const imageData = useMemo(() => {
    if (output.type !== "dashboard_image") return null;
    const d = output.data as
      | { url?: string; generated_at?: string | null; model?: string | null }
      | undefined;
    if (!d || typeof d.url !== "string" || !d.url) return null;
    return { url: d.url, generatedAt: d.generated_at ?? null, model: d.model ?? null };
  }, [output]);

  const html = useMemo(() => {
    if (imageData) return "";
    if (output.type === "dashboard") return String(output.data ?? "");
    const raw = String(output.data ?? "");
    const m = raw.match(
      /<div\s+data-xynth-component="dashboard">([\s\S]*)<\/div>\s*$/i,
    );
    return m ? m[1] : raw;
  }, [output, imageData]);

  // Observability for the one previously-blind hop: if a dashboard cell ever
  // shows nothing again, this tells us WHICH path ran and whether the HTML we
  // handed to the iframe was empty (regex stripped everything / wrapper
  // mismatch) vs the image path. Without it, hasDashboard/forceDashboardOnly
  // log green but the actual render is opaque.
  useEffect(() => {
    alertLog("dashboard:mini_render", {
      path: imageData ? "image" : "html",
      output_type: output.type,
      htmlLen: html.length,
      htmlEmpty: !imageData && html.trim().length === 0,
      imageUrlPresent: Boolean(imageData?.url),
    });
  }, [imageData, html, output.type]);

  return (
    <div
      className="relative rounded-md border border-slate-200/20 bg-slate-950/70 overflow-hidden shadow-inner pointer-events-none select-none"
      style={{ maxHeight: shellMax, minHeight: 80 }}
    >
      {imageData ? (
        <DashboardImage
          url={imageData.url}
          generatedAt={imageData.generatedAt}
          model={imageData.model}
          noBorder
        />
      ) : (
        <DashboardView html={html} noBorder />
      )}
      {/* Soft bottom fade so the clamped dashboard cuts off cleanly. */}
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-b from-transparent to-slate-950/95"
      />
    </div>
  );
}

/**
 * SummaryView — renders the rich "What it does / What it produces" markdown
 * generated by the backend (`alert_explainer._CELL_SYSTEM`). If the
 * description hasn't been generated yet (e.g. legacy alert still being
 * lazily backfilled), we fall back to the cell's short `purpose` title so
 * the tab is never silently empty.
 *
 * We deliberately avoid a full markdown library here — the backend
 * grammar is tightly constrained to `**Headings**` + plain paragraphs +
 * numbered/bullet lists, so a small hand-rolled parser keeps the bundle
 * lean and guarantees consistent styling across every node.
 */
function SummaryView({
  description,
  fallbackTitle,
  shellMax,
  viewMode,
}: {
  description?: string | null;
  fallbackTitle: string;
  shellMax: number;
  viewMode?: string;
}) {
  const blocks = useMemo<SummaryBlock[]>(() => {
    const md = (description || "").trim();
    if (!md) {
      // Fallback: show the cell title. Formatted as a single paragraph so
      // the visual frame reads like the real summary, just less content.
      return fallbackTitle && fallbackTitle.trim()
        ? [{ kind: "paragraph", text: fallbackTitle.trim() }]
        : [];
    }
    return parseSummaryMarkdown(md);
  }, [description, fallbackTitle]);

  return (
    <div
      className="relative rounded-md border border-slate-200/20 bg-slate-950/70 overflow-hidden"
      style={{ maxHeight: shellMax, minHeight: 80 }}
    >
      <div className="flex items-start gap-2 px-3 py-2.5">
        <span className={`shrink-0 inline-flex items-center justify-center w-5 h-5 rounded-md border mt-0.5 ${viewMode === "signal" ? "bg-emerald-300/10 border-emerald-300/25" : "bg-sky-300/10 border-sky-300/25"}`}>
          <FileText className={`w-3 h-3 ${viewMode === "signal" ? "text-emerald-200" : "text-sky-200"}`} strokeWidth={2} />
        </span>
        <div
          className="min-w-0 flex-1 overflow-hidden"
          style={{ maxHeight: shellMax - 20 }}
        >
          {blocks.length === 0 ? (
            <p className="text-[12px] leading-snug text-slate-100">—</p>
          ) : (
            <div className="flex flex-col gap-1.5">
              {blocks.map((b, i) => (
                <SummaryBlockView key={i} block={b} viewMode={viewMode} />
              ))}
            </div>
          )}
        </div>
      </div>
      {/* Soft fade at the bottom so long summaries cut off cleanly. */}
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-0 left-0 right-0 h-6 bg-gradient-to-b from-transparent to-slate-950/90"
      />
    </div>
  );
}

type SummaryBlock =
  | { kind: "heading"; text: string }
  | { kind: "paragraph"; text: string }
  | { kind: "list"; ordered: boolean; items: string[] };

/**
 * Minimal markdown parser scoped to the backend's constrained output:
 *   - `**Bold line**` → heading
 *   - `1. item` / `- item` / `* item` → list item (runs coalesce)
 *   - anything else → paragraph
 * We also strip inline `**bold**` spans inside paragraphs/list items and
 * let the render layer re-bold them so the weight is consistent.
 */
function parseSummaryMarkdown(md: string): SummaryBlock[] {
  const lines = md.split(/\r?\n/);
  const blocks: SummaryBlock[] = [];
  let pendingList: { ordered: boolean; items: string[] } | null = null;
  const flushList = () => {
    if (pendingList && pendingList.items.length > 0) {
      blocks.push({ kind: "list", ...pendingList });
    }
    pendingList = null;
  };
  for (const rawLine of lines) {
    const line = rawLine.trim();
    if (!line) {
      flushList();
      continue;
    }
    // Heading: a whole line that is just **...**
    const headingMatch = line.match(/^\*\*([^*].*?)\*\*\s*:?\s*$/);
    if (headingMatch) {
      flushList();
      blocks.push({ kind: "heading", text: headingMatch[1].trim() });
      continue;
    }
    const ordered = /^\d+[.)]\s+/.test(line);
    const bulleted = /^[-*]\s+/.test(line);
    if (ordered || bulleted) {
      const text = line.replace(/^(?:\d+[.)]|[-*])\s+/, "").trim();
      if (!pendingList || pendingList.ordered !== ordered) {
        flushList();
        pendingList = { ordered, items: [] };
      }
      pendingList.items.push(text);
      continue;
    }
    flushList();
    blocks.push({ kind: "paragraph", text: line });
  }
  flushList();
  return blocks;
}

function SummaryBlockView({ block, viewMode }: { block: SummaryBlock; viewMode?: string }) {
  if (block.kind === "heading") {
    return (
      <p className={`text-[10.5px] font-semibold uppercase tracking-[0.14em] leading-snug ${viewMode === "signal" ? "text-emerald-200/90" : "text-sky-200/90"}`}>
        {block.text}
      </p>
    );
  }
  if (block.kind === "paragraph") {
    return (
      <p className="text-[11.5px] leading-snug text-slate-100">
        <InlineMarkdown text={block.text} />
      </p>
    );
  }
  // list
  const ListTag = block.ordered ? "ol" : "ul";
  return (
    <ListTag
      className={[
        "text-[11.5px] leading-snug text-slate-100",
        block.ordered ? "list-decimal pl-4" : "list-disc pl-4",
        viewMode === "signal" ? "marker:text-emerald-200/60" : "marker:text-sky-200/60",
      ].join(" ")}
    >
      {block.items.map((item, i) => (
        <li key={i} className="mb-0.5">
          <InlineMarkdown text={item} />
        </li>
      ))}
    </ListTag>
  );
}

/**
 * Render `**bold**` spans in a line without pulling in a markdown lib.
 * Anything else is passed through as plain text.
 */
function InlineMarkdown({ text }: { text: string }) {
  const parts = useMemo(() => {
    const segments: Array<{ bold: boolean; value: string }> = [];
    const re = /\*\*([^*]+)\*\*/g;
    let last = 0;
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) {
        segments.push({ bold: false, value: text.slice(last, m.index) });
      }
      segments.push({ bold: true, value: m[1] });
      last = m.index + m[0].length;
    }
    if (last < text.length) {
      segments.push({ bold: false, value: text.slice(last) });
    }
    return segments;
  }, [text]);
  return (
    <>
      {parts.map((p, i) =>
        p.bold ? (
          <strong key={i} className="text-slate-50 font-semibold">
            {p.value}
          </strong>
        ) : (
          <span key={i}>{p.value}</span>
        ),
      )}
    </>
  );
}

/**
 * CodeView — shows the full cell code with a slow auto-scroll loop
 * (teleprompter / film-credits style). Content scrolls upward at a
 * gentle pace; when it finishes its first pass it seamlessly loops
 * back to the start. We double the content and translate -50% so the
 * loop is visually continuous (no jump).
 *
 * Intersection / visibility pausing is handled at the carousel level
 * via `paused` / `visible` state — when the content isn't on screen we
 * don't burn CPU on the keyframe animation (the browser throttles
 * hidden animations, but we also respect prefers-reduced-motion).
 */
function CodeView({ code, shellMax, viewMode }: { code: string; shellMax: number; viewMode?: string }) {
  const full = useMemo(() => code?.trim() || "# (empty)", [code]);

  // Only loop if the content meaningfully exceeds the shell.
  // We approximate "does it overflow" by line count * lineHeight >
  // shell's scrollable area.
  const lines = useMemo(() => full.split("\n"), [full]);
  const lineHeightPx = 17;
  const overflowThreshold = Math.max(6, Math.floor((shellMax - 36) / lineHeightPx));
  const shouldScroll = lines.length > overflowThreshold;

  // Slow scroll — 15px/sec per user. Duration = pixels / rate.
  // We're translating one copy of the content by its own height, so
  // duration ~= (lines * lineHeight) / 15.
  const scrollPxTotal = lines.length * lineHeightPx;
  const SCROLL_RATE_PX_PER_SEC = 15;
  const scrollDurationSec = Math.max(
    10,
    Math.floor(scrollPxTotal / SCROLL_RATE_PX_PER_SEC),
  );
  const animClassId = `code-scroll-${scrollDurationSec}s`;

  return (
    <div
      className="relative rounded-md border border-slate-200/20 bg-slate-950/80 overflow-hidden"
      style={{ maxHeight: shellMax, minHeight: 80 }}
    >
      <div className="flex items-center gap-1.5 px-3 pt-2 pb-1.5">
        <Code2 className={`w-3 h-3 ${viewMode === "signal" ? "text-emerald-200/80" : "text-sky-200/80"}`} strokeWidth={2} />
        <span className={`text-[10px] font-semibold uppercase tracking-[0.14em] ${viewMode === "signal" ? "text-emerald-200/80" : "text-sky-200/80"}`}>
          Code
        </span>
      </div>
      <div
        className="relative px-1"
        style={{ height: shellMax - 36, overflow: "hidden" }}
      >
        {shouldScroll ? (
          <div className={`code-scroll-track ${animClassId}`}>
            <CodeBlock code={full} />
            {/* Second copy so the loop is seamless. */}
            <CodeBlock code={full} />
          </div>
        ) : (
          <CodeBlock code={full} />
        )}
      </div>
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-b from-transparent to-slate-950/95"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute top-[30px] left-0 right-0 h-6 bg-gradient-to-b from-slate-950/90 to-transparent"
      />
      <style jsx>{`
        .code-scroll-track {
          animation: codeScroll ${scrollDurationSec}s linear infinite;
          will-change: transform;
        }
        @keyframes codeScroll {
          from {
            transform: translateY(0);
          }
          to {
            /* Translate by exactly half the doubled-content height so
               loop lands frame-perfect on the start of the second copy. */
            transform: translateY(-50%);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .code-scroll-track {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
}

function CodeBlock({ code }: { code: string }) {
  return (
    <SyntaxHighlighter
      language="python"
      style={materialDark}
      customStyle={{
        background: "transparent",
        padding: "2px 10px 8px",
        margin: 0,
        fontSize: "10.5px",
        lineHeight: "17px",
      }}
      codeTagProps={{
        style: { fontFamily: "ui-monospace, SFMono-Regular, monospace" },
      }}
    >
      {code}
    </SyntaxHighlighter>
  );
}

function StdoutView({ text, shellMax, viewMode }: { text: string; shellMax: number; viewMode?: string }) {
  // Scrub out Plotly's text/plain placeholder so the Stdout view never
  // displays noise lines like "[Plotly Figure]" (backend emits this
  // alongside every Plotly chart; see PKA templates.py).
  const content = useMemo(() => {
    const scrubbed = (text || "")
      .split("\n")
      .filter((line) => !isNoiseStdoutText(line))
      .join("\n");
    return scrubbed.trim() || "(no output)";
  }, [text]);

  // Overflow detection + teleprompter-style auto-scroll — mirrors the
  // CodeView / DataframePreview pattern. Long stdout (loops, training
  // logs, rich debug dumps) was previously clipped at the shell height
  // and the tail of the output was never visible on the canvas. Now we
  // estimate line count, decide if the content exceeds the viewport,
  // and if so animate a doubled copy at 15 px/sec (same rate as
  // CodeView). Matching rates across Code / Stdout / Dataframe keeps
  // the visual cadence coherent across tabs.
  const lineHeightPx = 16; // 10.5px font × 1.55 line-height ≈ 16.3
  const lines = useMemo(() => content.split("\n"), [content]);
  const viewportPx = shellMax - 30; // header is 30px tall
  const overflowThreshold = Math.max(
    6,
    Math.floor(viewportPx / lineHeightPx),
  );
  const shouldScroll = lines.length > overflowThreshold;

  const scrollPxTotal = lines.length * lineHeightPx;
  const SCROLL_RATE_PX_PER_SEC = 15;
  const scrollDurationSec = Math.max(
    10,
    Math.floor(scrollPxTotal / SCROLL_RATE_PX_PER_SEC),
  );
  const animClassId = `stdout-scroll-${scrollDurationSec}s`;

  return (
    <div
      className="relative rounded-md border border-slate-200/20 bg-slate-950/80 overflow-hidden"
      style={{ maxHeight: shellMax, minHeight: 80 }}
    >
      <div className="flex items-center gap-1.5 px-3 pt-2 pb-1.5">
        <Terminal className={`w-3 h-3 ${viewMode === "signal" ? "text-emerald-200/80" : "text-sky-200/80"}`} strokeWidth={2} />
        <span className={`text-[10px] font-semibold uppercase tracking-[0.14em] ${viewMode === "signal" ? "text-emerald-200/80" : "text-sky-200/80"}`}>
          Stdout
        </span>
      </div>
      <div
        className="relative"
        style={{ height: viewportPx, overflow: "hidden" }}
      >
        {shouldScroll ? (
          <div className={`stdout-scroll-track ${animClassId}`}>
            <pre className="px-3 pb-2 font-mono text-[10.5px] leading-[1.55] text-slate-100 whitespace-pre-wrap break-words m-0">
              {content}
            </pre>
            {/* Second copy so the translateY(-50%) loop is seamless. */}
            <pre
              aria-hidden
              className="px-3 pb-2 font-mono text-[10.5px] leading-[1.55] text-slate-100 whitespace-pre-wrap break-words m-0"
            >
              {content}
            </pre>
          </div>
        ) : (
          <pre className="px-3 pb-2 font-mono text-[10.5px] leading-[1.55] text-slate-100 whitespace-pre-wrap break-words m-0">
            {content}
          </pre>
        )}
      </div>
      <div
        aria-hidden
        className="pointer-events-none absolute bottom-0 left-0 right-0 h-8 bg-gradient-to-b from-transparent to-slate-950/95"
      />
      <style jsx>{`
        .stdout-scroll-track {
          animation: stdoutScroll ${scrollDurationSec}s linear infinite;
          will-change: transform;
        }
        @keyframes stdoutScroll {
          from {
            transform: translateY(0);
          }
          to {
            transform: translateY(-50%);
          }
        }
        @media (prefers-reduced-motion: reduce) {
          .stdout-scroll-track {
            animation: none;
          }
        }
      `}</style>
    </div>
  );
}
